/* ============================================================================
   rcds-motion.js — UNIVERSAL RolaCase US motion engine (every page).
   ----------------------------------------------------------------------------
   Replaces the homepage-only reveal in rcds-home.js. Three features, all
   progressive-enhancement + SAFE-BY-DEFAULT:

   1) SCROLL REVEAL — elements with .rcds-reveal fade + slide up on entry
      (IntersectionObserver threshold .12, rootMargin -8%, + top<0 catch-up +
      a throttled scroll/resize/load sweep failsafe). Gated on the .rcds-js
      class this script adds to <html> FIRST — so the opacity:0 start-state
      (scoped .rcds-js in rcds-base.css) ONLY applies while JS runs. If this
      file 404s / is CSP-blocked / JS is off, .rcds-js is never added and every
      .rcds-reveal stays fully visible. prefers-reduced-motion reveals all.

   2) GROUP STAGGER — any [data-rcds-reveal-group] has .rcds-reveal added to
      each direct child with --rcds-rvl-i set to its index, so grids of cards
      cascade in instead of firing together. No per-card Liquid edits needed.

   3) HERO PARALLAX — [data-rcds-parallax] background media gets a subtle
      rAF-throttled translateY while its section is in view (reduced-motion +
      no-image both no-op).

   No deps, no globals (IIFE). Enqueued once from snippets/rcds-nav.liquid,
   which renders on every template.
   ========================================================================== */
(function () {
  'use strict';
  var root = document.documentElement;
  root.classList.add('rcds-js');

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---- group stagger: tag children of each reveal-group ------------------- */
  function tagGroups() {
    all('[data-rcds-reveal-group]').forEach(function (grp) {
      var kids = Array.prototype.slice.call(grp.children);
      kids.forEach(function (kid, i) {
        kid.classList.add('rcds-reveal');
        kid.style.setProperty('--rcds-rvl-i', String(i % 8));
      });
    });
  }

  /* ---- 1) scroll reveal --------------------------------------------------- */
  function initReveal() {
    var els = all('.rcds-reveal');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('is-rcds-in'); });
      return;
    }
    function show(el) { el.classList.add('is-rcds-in'); }
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (en) {
        if (en.isIntersecting || en.boundingClientRect.top < 0) { show(en.target); io.unobserve(en.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (e) { io.observe(e); });

    var ticking = false;
    function sweep() {
      ticking = false;
      var vh = window.innerHeight;
      els.forEach(function (el) {
        if (el.classList.contains('is-rcds-in')) return;
        if (el.getBoundingClientRect().top < vh * 0.92) { show(el); io.unobserve(el); }
      });
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(sweep); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', sweep);
    sweep();
  }

  /* ---- 3) hero parallax --------------------------------------------------- */
  function initParallax() {
    if (reduce || !('IntersectionObserver' in window)) return;
    all('[data-rcds-parallax]').forEach(function (media) {
      var section = media.closest('section') || media.parentElement;
      var visible = false, ticking = false;
      var vio = new IntersectionObserver(function (ents) {
        visible = ents[0].isIntersecting;
        if (visible) tick();
      }, { threshold: 0 });
      vio.observe(section);
      function apply() {
        ticking = false;
        var rect = section.getBoundingClientRect();
        var off = Math.max(-40, Math.min(40, rect.top * -0.08));
        media.style.transform = 'translate3d(0,' + off.toFixed(1) + 'px,0) scale(1.08)';
      }
      function tick() { if (!ticking && visible) { ticking = true; requestAnimationFrame(apply); } }
      window.addEventListener('scroll', tick, { passive: true });
      apply();
    });
  }

  /* ---- 4) count-up for numeric stats (opt-in via [data-rcds-countup]) ------
     Reads the target from the element's own text (server-rendered final value),
     so with no JS the real number is already on screen. prefers-reduced-motion
     and no-IntersectionObserver both jump straight to the final value. One-shot. */
  function initCountUp() {
    var els = all('[data-rcds-countup]');
    if (!els.length) return;
    function fmt(v, d) { return d > 0 ? v.toFixed(d) : String(Math.round(v)); }
    function run(el) {
      var raw = (el.getAttribute('data-rcds-countup-to') || el.textContent || '').replace(/,/g, '');
      var target = parseFloat(raw);
      if (isNaN(target)) return;
      var dec = parseInt(el.getAttribute('data-rcds-countup-decimals'), 10) || 0;
      var pre = el.getAttribute('data-rcds-countup-prefix') || '';
      var suf = el.getAttribute('data-rcds-countup-suffix') || '';
      if (reduce) { el.textContent = pre + fmt(target, dec) + suf; return; }
      var dur = parseInt(el.getAttribute('data-rcds-countup-dur'), 10) || 1200;
      var t0 = null;
      function step(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = pre + fmt(target * eased, dec) + suf;
        if (p < 1) { requestAnimationFrame(step); } else { el.textContent = pre + fmt(target, dec) + suf; }
      }
      requestAnimationFrame(step);
    }
    if (reduce || !('IntersectionObserver' in window)) { els.forEach(run); return; }
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (en) { if (en.isIntersecting) { run(en.target); io.unobserve(en.target); } });
    }, { threshold: 0.6 });
    els.forEach(function (e) { io.observe(e); });
  }

  /* ---- 5) white-header elevation on scroll (#12) -------------------------
     The header is unconditionally opaque white; this just adds .is-rcds-scrolled
     once the page scrolls so the bar reads as lifted above the dark sections
     passing under it. Passive + rAF-throttled. No JS -> header is simply flat. */
  function initHeaderScroll() {
    var nav = document.querySelector('.rcds-nav');
    if (!nav) return;
    var header = nav.closest('.header');            // the sticky wrapper (div.header) — common ancestor of promo + nav
    // On-page Find My Fit card (homepage, trade collections, budget page, Fit Your Van). A page can carry a
    // desktop card and a phone card; only the one shown at this width counts (a hidden one measures 0).
    var cards = document.querySelectorAll('.rcds-ff');
    function visibleCard() { for (var i = 0; i < cards.length; i++) if (cards[i].offsetWidth) return cards[i]; return null; }
    var ticking = false;
    var collapsed = false;               // hysteresis state, persists across scroll frames
    var COLLAPSE_AT = 56;                // collapse only once scrolled past the ~44px promo strip (+ margin)
    var OPEN_AT = 8;                     // re-open only right at the top; the 8..56 dead-band stops jitter re-toggling
    function apply() {
      ticking = false;
      var y = window.pageYOffset;
      if (collapsed) { if (y < OPEN_AT) { collapsed = false; } }
      else if (y > COLLAPSE_AT) { collapsed = true; }
      var scrolled = collapsed;
      nav.classList.toggle('is-rcds-scrolled', scrolled);
      if (header) { header.classList.toggle('is-rcds-scrolled', scrolled); } // collapses the promo (#2), keeps header shadow (#12)
      /* #40/#12 nav CTA reveal. The header "Find My Fit-Out" duplicates the button
         inside the on-page fit-finder, so it stays hidden while the fit-finder is in
         view (its own button serves) and fades in only once the fit-finder's bottom
         has scrolled up behind the header — the two never show at once. Live geometry,
         so it survives the promo collapse (header height changes) and any resize. On
         pages with no fit-finder the CTA just follows the scrolled state. */
      var showCta, fitbar = visibleCard();
      if (fitbar) {
        var hb = header ? header.getBoundingClientRect().bottom : 0;
        showCta = fitbar.getBoundingClientRect().bottom <= hb;
      } else {
        showCta = scrolled;
      }
      nav.classList.toggle('is-rcds-cta', showCta);
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(apply); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    apply();
  }

  /* ---- 6) hero background video autoplay (opt-in via [data-rcds-hero-video]) ---
     The <video> ships PAUSED showing its poster (= the hero image), so with NO JS
     or with prefers-reduced-motion the trade hero is byte-for-byte the static image
     hero. Only when motion is allowed do we call .play(); the video is muted +
     playsinline so the browser permits programmatic autoplay (incl. iOS). A rejected
     play() promise (autoplay blocked / iOS Low Power Mode) is swallowed so the poster
     simply stays. Early-returns under reduced-motion or when there is no hero video. */
  function initHeroVideo() {
    if (reduce) return;
    var vids = all('video[data-rcds-hero-video]');
    if (!vids.length) return;
    vids.forEach(function (v) {
      v.muted = true; // set the PROPERTY (Safari can ignore the muted attribute for autoplay)
      var p = v.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    });
  }

  /* ---- 7) quote-list add (product cards) --------------------------------
     The card CTA is a real <form action="/cart/add"> so with NO JS it POSTs and
     Shopify redirects to /cart with the item added. This enhances that path:
     intercept the submit, add via /cart/add.js (no navigation), confirm with a
     toast and bump the header cart-count badge. ANY failure falls through to a
     native form.submit() so the item is never lost. Quote-safe: every line is
     sent with properties.ItemType='Quote'; nothing here renders a price or a
     checkout control. Same-origin, XSS-safe DOM (createElement + textContent). */
  function cartCountEls() { return all('[data-rcds-cart-count]'); }
  function setCartCount(n) {
    cartCountEls().forEach(function (el) {
      el.textContent = String(n);
      if (n > 0) { el.removeAttribute('hidden'); } else { el.setAttribute('hidden', ''); }
    });
  }
  function initCartBadge() {
    if (!cartCountEls().length || !window.fetch) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c && typeof c.item_count === 'number') { setCartCount(c.item_count); } })
      .catch(function () {});
  }

  var quoteToastTimer = null;
  var TOAST_MS = 3500;
  var TOAST_ERROR_MS = 9000;  // a refusal is read, not glanced at
  var TOAST_FADE_MS = 320;
  function showToast(text, linkText, linkHref, isError) {
    var prev = document.querySelector('.rcds-toast');
    if (prev && prev.parentNode) { prev.parentNode.removeChild(prev); }
    var toast = document.createElement('div');
    toast.className = isError ? 'rcds-toast rcds-toast--error' : 'rcds-toast';
    toast.setAttribute('role', isError ? 'alert' : 'status');
    toast.setAttribute('aria-live', isError ? 'assertive' : 'polite');
    var msg = document.createElement('span');
    msg.className = 'rcds-toast__msg';
    msg.textContent = text;
    toast.appendChild(msg);
    if (linkText && linkHref) {
      var link = document.createElement('a');
      link.className = 'rcds-toast__link';
      link.href = linkHref;
      link.textContent = linkText;
      toast.appendChild(link);
    }
    function hide() {
      window.clearTimeout(quoteToastTimer);
      toast.classList.remove('is-rcds-in');
      window.setTimeout(function () { if (toast.parentNode) { toast.parentNode.removeChild(toast); } }, TOAST_FADE_MS);
    }
    function arm() {
      window.clearTimeout(quoteToastTimer);
      quoteToastTimer = window.setTimeout(hide, isError ? TOAST_ERROR_MS : TOAST_MS);
    }
    if (isError) {
      // A refusal carries the only way forward (its link), so it can be dismissed by hand and it
      // never times out while the pointer or the keyboard focus is on it.
      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'rcds-toast__close';
      close.setAttribute('aria-label', 'Dismiss');
      close.textContent = '\u00d7';
      close.addEventListener('click', hide);
      toast.appendChild(close);
      toast.addEventListener('mouseenter', function () { window.clearTimeout(quoteToastTimer); });
      toast.addEventListener('focusin', function () { window.clearTimeout(quoteToastTimer); });
      toast.addEventListener('mouseleave', arm);
      toast.addEventListener('focusout', arm);
    }
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('is-rcds-in'); });
    arm();
  }
  function showQuoteToast() { showToast('Added to your quote list', 'View quote list', '/cart', false); }

  /* A cart add Shopify REFUSED (422 "already sold out", a quantity limit, 429 too many attempts).
     Show Shopify's own reason and stay on the page. The old fallback re-posted the same form
     natively, which Shopify refuses the same way, so the shopper landed on a bare error page
     (measured 2026-09-30). A NETWORK failure still falls back to the native POST in initQuoteAdd
     below; the buy-together add shows the error toast instead (its native form holds one row). */
  var REFUSED = {};
  var PARTIAL_ADD = /added to your cart/i;  // Shopify adds what is in stock and still answers 422
  var ADD_FAILED = 'This could not be added to your cart. Please try again.';  // Shopify gave no reason
  var CART_REFUSED_EVENT = 'rcds:cart-refused';  // fired on document once per refused add
  // A package row's id input, items[<n>][id]; <n> pairs it with that row's items[<n>][quantity].
  var PKG_ITEM_ID_NAME = /^items\[(\d+)\]\[id\]$/;
  // An input's value as a whole number of at least 1, else `fallback` (missing input, blank, 0, junk).
  function positiveIntOr(input, fallback) {
    var n = input ? parseInt(input.value, 10) : NaN;
    return isNaN(n) || n < 1 ? fallback : n;
  }
  // The quantity input a press reads: the form's own, else for the mobile sticky bar (a quote product
  // keeps the bar's own form, which has no stepper) the stepper of the buy box above it, else null.
  function qtyInputFor(form) {
    var own = form.querySelector('[name="quantity"]');
    if (own || !form.closest || !form.closest('.rcds-quotebar')) { return own; }
    var section = form.closest('.rcds-product-main');
    return section ? section.querySelector('.rcds-product__info form[data-rcds-quoteform] [name="quantity"]') : null;
  }
  // Where "Request a quote" should go: this page's own form when the refused form is this page's
  // buy box or mobile bar, the refused product's page when it was a card or a quick view, else nowhere
  // (a buy-together add names several products, so no single form fits).
  function quoteHrefFor(form) {
    if (!form || !form.closest) { return ''; }
    if (form.closest('.rcds-product__info .rcds-quote') || form.closest('.rcds-quotebar')) {
      return document.getElementById('rcds-quote-form') ? '#rcds-quote-form' : '';
    }
    var holder = form.closest('.rcds-product-card') || form.closest('.rcds-qv-panel');
    var link = holder && holder.querySelector('a[href*="/products/"]');
    return link ? link.getAttribute('href').split('#')[0] + '#rcds-quote-form' : '';
  }
  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (c && typeof c.item_count === 'number') { setCartCount(c.item_count); }
        document.dispatchEvent(new CustomEvent('rcds:cart-updated'));
      })
      .catch(function () {});
  }
  function refused(form, body) {
    var text = body && (body.description || body.message);
    if (typeof text !== 'string' || !text) { text = ADD_FAILED; }
    // Said BEFORE the cart is re-read: the dealer layer (rcds-dealer-prices.js) takes a cart change soon
    // after a dealer's press as "added", so it has to hear first that this press was refused.
    document.dispatchEvent(new CustomEvent(CART_REFUSED_EVENT));
    refreshCart();
    if (PARTIAL_ADD.test(text)) {
      showToast(text, 'View cart', '/cart', false);
    } else {
      var href = quoteHrefFor(form);
      showToast(text, href ? 'Request a quote' : '', href, true);
    }
    return REFUSED;
  }
  function readAdd(form) {
    return function (r) {
      if (r.ok) { return r.json(); }
      return r.json().then(function (body) { return refused(form, body); }, function () { return refused(form, null); });
    };
  }

  /* ⛔ A PACKAGE ADD IS ALL OR NOTHING (2026-10-31). A package posts its parts as one items[] batch and
     Shopify fills it line by line: a part with too little stock goes in at what is left, the other parts go
     in whole, and the answer is still a 422 ("Only available quantity items were added to your cart."). The
     cart then held, say, three partitions beside two driver sides. So the cart is read before a package add
     and again after a refusal, every line of this package that grew is put back to what it held before
     (/cart/change.js, one line per call), and ONE toast follows: how many packages the stock allows when it
     allows some, else Shopify's own reason while it is still true, else a plain sentence.
     A read or a change the store refuses once (its bot check answers 429 to a burst) is tried once more
     after UNDO_RETRY_MS; if the undo still cannot finish, the toast says the cart may hold part of the
     package, as an error, never in Shopify's "added" words.
     ⛔ A QUOTE IS NEVER CAPPED BY STOCK (Sophia 2026-10-30, "please fix asap"). A Request a Quote press
     on a package whose side bundle Shopify caps (FTM130: RSMRSAM-DR and -PA tracked at 2) used to end
     on Shopify's "The maximum quantity of this item is already in your cart". A quoted package is priced
     and built to order, so a refused quote add is undone like any other and the shopper is taken to a
     quote form with a neutral line: this page's own, which the builder fills with the parts chosen, or,
     from a quick view or a card, the product's page. Shopify's cart words never reach a quote press.
     The number of packages asked for goes with it (contact[Quantity] on the form, rcds_quote_qty on the
     link to the product's page), so a request for three sets never arrives as a request for one. */
  var PKG_FITS_TEXT = 'Only {n} of this package can be added right now.';
  var PKG_NOT_ADDED_TEXT = 'This package could not be added in that quantity right now.';
  var PKG_UNDO_INCOMPLETE_TEXT = 'Part of this package may be in your cart. Check your cart before ordering.';
  var PKG_QUOTE_FORM_TEXT = 'Send your request for {pkg} with the form below.';
  var PKG_QUOTE_PAGE_TEXT = 'Send your request for {pkg} on its product page.';
  var PKG_ONE_TEXT = 'this package';
  var PKG_MANY_TEXT = '{n} of this package';
  var PKG_QTY_FIELD = 'contact[Quantity]';           // the quote form field that carries the package count
  var PKG_QTY_VALUE_TEXT = '{n} packages';
  var PKG_QTY_PARAM = 'rcds_quote_qty';               // the same count on a link to the product's page
  var PKG_QTY_PARAM_MAX = 9999;                        // a link asking for more is not a count anyone chose
  var QUOTE_FORM_ID = 'rcds-quote-form';
  var UNDO_RETRY_MS = 1000;
  var ITEM_TYPE_QUOTE = 'Quote';
  // Shopify's reason in a refused add's body, or ''.
  function reasonOf(body) {
    var text = body && (body.description || body.message);
    return typeof text === 'string' ? text : '';
  }
  function later(ms) { return new Promise(function (done) { window.setTimeout(done, ms); }); }
  // The cart's lines, or null when the cart cannot be read. Never rejects.
  function cartItems() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { return c && Array.isArray(c.items) ? c.items : null; })
      .catch(function () { return null; });
  }
  function qtyByKey(lines) {
    var out = {};
    lines.forEach(function (l) { out[l.key] = l.quantity; });
    return out;
  }
  function setLineQty(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { if (!r.ok) { throw new Error('cart/change.js ' + r.status); } });
  }
  // cartItems and setLineQty, each tried once more after UNDO_RETRY_MS when the store refuses the first.
  function cartItemsRetried() {
    return cartItems().then(function (lines) { return lines || later(UNDO_RETRY_MS).then(cartItems); });
  }
  function setLineQtyRetried(key, quantity) {
    return setLineQty(key, quantity).catch(function () {
      return later(UNDO_RETRY_MS).then(function () { return setLineQty(key, quantity); });
    });
  }
  // The undo could not finish: the cart may hold part of a package. An error, with the way to check it.
  function undoIncomplete() {
    document.dispatchEvent(new CustomEvent(CART_REFUSED_EVENT));
    refreshCart();
    showToast(PKG_UNDO_INCOMPLETE_TEXT, 'View cart', '/cart', true);
    return REFUSED;
  }
  // 'this package', or '3 of this package' when more than one was asked for.
  function packagesText(template, pkgQty) {
    return template.replace('{pkg}', pkgQty > 1 ? PKG_MANY_TEXT.replace('{n}', String(pkgQty)) : PKG_ONE_TEXT);
  }
  // Writes the package count on a quote form (the box holding it), as the builder writes the parts chosen;
  // one package is what any request means, so then the field is taken off.
  function notePackageQty(box, pkgQty) {
    var form = box && box.querySelector('form');
    if (!form) { return; }
    var field = form.querySelector('input[name="' + PKG_QTY_FIELD + '"]');
    if (pkgQty <= 1) {
      if (field && field.parentNode) { field.parentNode.removeChild(field); }
      return;
    }
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = PKG_QTY_FIELD;
      form.appendChild(field);
    }
    field.value = PKG_QTY_VALUE_TEXT.replace('{n}', String(pkgQty));
  }
  // href (a product's page, ending in its quote form's #anchor) carrying the package count, read back by
  // initQuoteQtyFromLink on that page.
  function linkWithPackageQty(href, pkgQty) {
    if (!href || pkgQty <= 1) { return href; }
    var cut = href.indexOf('#');
    var path = cut < 0 ? href : href.slice(0, cut);
    var anchor = cut < 0 ? '' : href.slice(cut);
    return path + (path.indexOf('?') < 0 ? '?' : '&') + PKG_QTY_PARAM + '=' + pkgQty + anchor;
  }
  // A refused QUOTE package, with a neutral line and never Shopify's cart words: to this page's quote form
  // when the press was this page's buy box or bar, else a link to the product's page (a quick view or a card).
  function quoteByForm(form, pkgQty) {
    var href = quoteHrefFor(form);
    var target = href.charAt(0) === '#' ? document.getElementById(href.slice(1)) : null;
    document.dispatchEvent(new CustomEvent(CART_REFUSED_EVENT));
    refreshCart();
    if (target) {
      notePackageQty(target, pkgQty);
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      showToast(packagesText(PKG_QUOTE_FORM_TEXT, pkgQty), '', '', false);
      return REFUSED;
    }
    var link = linkWithPackageQty(href, pkgQty);
    showToast(packagesText(PKG_QUOTE_PAGE_TEXT, pkgQty), link ? 'Request a quote' : '', link, false);
    return REFUSED;
  }
  // A product page opened from that link: its quote form carries the count asked for.
  function initQuoteQtyFromLink() {
    var m = new RegExp('[?&]' + PKG_QTY_PARAM + '=([0-9]+)').exec(window.location.search || '');
    var pkgQty = m ? parseInt(m[1], 10) : NaN;
    if (!(pkgQty > 1 && pkgQty <= PKG_QTY_PARAM_MAX)) { return; }
    notePackageQty(document.getElementById(QUOTE_FORM_ID), pkgQty);
  }
  // need: units of each variant in ONE package. before: the cart's quantity per line key ahead of the add,
  // or null when it could not be read (nothing can be told apart then: no undo). quote: the press asked for a
  // quote. Resolves REFUSED once the toast is up; never rejects, so a refusal never reaches the native re-post.
  function undoPackageAdd(form, need, pkgQty, before, body, quote) {
    var settle = function () { return quote ? quoteByForm(form, pkgQty) : refused(form, body); };
    // Shopify's "added" words with nothing to undo against: part of the package may have landed.
    var unknown = function () { return PARTIAL_ADD.test(reasonOf(body)) ? undoIncomplete() : settle(); };
    if (!before) { return unknown(); }
    return cartItemsRetried().then(function (after) {
      if (!after) { return unknown(); }
      var was = function (l) { return before[l.key] || 0; };
      var grew = after.filter(function (l) { return need[l.variant_id] && l.quantity > was(l); });
      if (!grew.length) { return settle(); }  // nothing landed: Shopify's own reason, or the quote form
      var added = {};
      grew.forEach(function (l) { added[l.variant_id] = (added[l.variant_id] || 0) + l.quantity - was(l); });
      var fits = Infinity;
      Object.keys(need).forEach(function (id) { fits = Math.min(fits, Math.floor((added[id] || 0) / need[id])); });
      return grew.reduce(function (done, l) {
        return done.then(function () { return setLineQtyRetried(l.key, was(l)); });
      }, Promise.resolve()).then(function () {
        if (quote) { return quoteByForm(form, pkgQty); }
        var text = reasonOf(body);
        if (fits >= 1 && fits < pkgQty) { text = PKG_FITS_TEXT.replace('{n}', String(fits)); }
        else if (!text || PARTIAL_ADD.test(text)) { text = PKG_NOT_ADDED_TEXT; }
        return refused(form, { description: text });
      }, undoIncomplete);
    }).catch(undoIncomplete);
  }

  // Quantity stepper: +/- buttons around a [name="quantity"] number input. Delegated,
  // idempotent, no-JS-safe (the number input types directly without the buttons).
  function initQtyStepper() {
    if (window.__rcQtyBound) return;
    window.__rcQtyBound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-rcds-qty-dec],[data-rcds-qty-inc]') : null;
      if (!btn) return;
      var wrap = btn.closest('[data-rcds-qty]');
      var input = wrap && wrap.querySelector('[name="quantity"]');
      if (!input) return;
      e.preventDefault();
      var n = parseInt(input.value, 10); if (isNaN(n) || n < 1) n = 1;
      n = btn.hasAttribute('data-rcds-qty-inc') ? n + 1 : Math.max(1, n - 1);
      input.value = n;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }, false);
  }

  function initQuoteAdd() {
    if (window.__rcQuoteBound || !window.fetch) return;  // no fetch -> native form POST handles it
    window.__rcQuoteBound = true;
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('[data-rcds-quoteform]')) return;
      // Live variant: on a PDP / quick-view the variant picker updates a hidden
      // [data-rcds-variant-id] inside the same .rcds-quote; prefer it so the
      // SELECTED variant is quoted. Cards have no picker -> fall back to the
      // form's own hidden id (the default variant, also what a no-JS POST sends).
      var scope = form.closest('.rcds-quote') || form;
      // MULTI-LINE ADD (package builder). When a form carries enabled item rows the cart
      // lines are the ticked components, not one parent product, so post them as an
      // items[] batch. Additive: a form without these rows is untouched, and the rows
      // are already correct for a native no-JS POST.
      // ItemType follows the control the customer actually pressed. Add to Cart carries
      // data-rcds-itemtype="Buy"; every other add control carries nothing and stays a
      // quote line. Default 'Quote' so a control we forget to label is quote-safe, never
      // silently converted into a purchase. Mirrors the Liquid no-JS path exactly.
      var rcSub = e.submitter || form.querySelector('[data-rcds-itemtype]') || form.querySelector('[data-rcds-goto]');
      var rcItemType = (rcSub && rcSub.getAttribute && rcSub.getAttribute('data-rcds-itemtype')) || 'Quote';
      if (rcItemType !== 'Buy') { rcItemType = 'Quote'; }
      var itemRows = Array.prototype.filter.call(
        form.querySelectorAll('[data-pb-item][name$="[id]"]'), function (n) { return !n.disabled; });
      if (itemRows.length) {
        var pkgName = (form.querySelector('[name$="[properties][Package]"]') || {}).value || '';
        // ⛔ PACKAGE QUANTITY (2026-10-25). Each line is (units of that component in ONE package,
        // the row's own items[n][quantity]) x (packages wanted, the form's stepper). This used to
        // send quantity 1 on every line, so a customer who chose 2 packages was charged for, and
        // the warehouse was sent, 1 of each component. The hidden per-row quantity stays the
        // per-package figure and is never rewritten here: rcds-dealer-prices.js reads it to price
        // ONE package, the same unit the retail price beside it shows. The mobile sticky bar's form
        // has no stepper and takes the buy box's (qtyInputFor); other forms with none (tv2 cards)
        // multiply by 1, exactly as before.
        var pkgQty = positiveIntOr(qtyInputFor(form), 1);
        // Units of a row's component in ONE package: the row's own items[n][quantity], else 1.
        var perPkg = function (n) {
          var m = PKG_ITEM_ID_NAME.exec(n.name || '');
          return m ? positiveIntOr(form.querySelector('[name="items[' + m[1] + '][quantity]"]'), 1) : 1;
        };
        var lineQty = function (n) { return perPkg(n) * pkgQty; };
        // What ONE package takes of each variant, for the all-or-nothing undo (undoPackageAdd).
        var need = {};
        var items = itemRows.map(function (n) {
          need[n.value] = (need[n.value] || 0) + perPkg(n);
          return { id: n.value, quantity: lineQty(n),
                   properties: { ItemType: rcItemType, Package: pkgName } };
        });
        // Native fallback (fetch failed): the served rows say 1 per package, so write the real
        // line quantities in first, or the fallback POST would repeat the old 1-of-each bug. The
        // page navigates away on submit, so the per-package figure is not needed afterwards.
        var nativeSubmit = function () {
          itemRows.forEach(function (n) {
            var m = PKG_ITEM_ID_NAME.exec(n.name || '');
            var q = m && form.querySelector('[name="items[' + m[1] + '][quantity]"]');
            if (q) { q.value = lineQty(n); }
          });
          form.submit();
        };
        e.preventDefault();
        // One package add at a time from a form: a second press while the first is out would read the
        // cart halfway through it, and its undo could take back lines the first press added.
        if (form.__rcdsPkgAdding) { return; }
        form.__rcdsPkgAdding = true;
        var sub = e.submitter || form.querySelector('[data-rcds-goto]');
        var to = sub && sub.getAttribute ? sub.getAttribute('data-rcds-goto') : null;
        cartItems().then(function (lines) {
          var before = lines ? qtyByKey(lines) : null;
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: items })
          }).then(function (r) {
            // A 200 whose body does not parse still added the lines, so it never earns a second (native) post.
            if (r.ok) { return r.json().catch(function () { return null; }); }
            var undo = function (body) {
              return undoPackageAdd(form, need, pkgQty, before, body, rcItemType === ITEM_TYPE_QUOTE);
            };
            return r.json().then(undo, function () { return undo(null); });
          });
        }).then(function (res) {
          form.__rcdsPkgAdding = false;
          if (res === REFUSED) { return; }
          if (to) { window.location.href = to; return; }
          // A quote-list add says so and moves the badge, as a single product's add does. This used to call a
          // toast that does not exist, so the press looked like nothing happened and was pressed again.
          showQuoteToast();
          refreshCart();
        }).catch(function () { form.__rcdsPkgAdding = false; nativeSubmit(); });
        return;
      }
      var liveVar = scope.querySelector('[data-rcds-variant-id]');
      var idField = form.querySelector('[name="id"]');
      var id = (liveVar && liveVar.value) || (idField && idField.value);
      if (!id) return;  // nothing to add -> let the native submit run
      // Quantity (priced PDPs carry a [name="quantity"], the sticky bar takes the buy box's; cards do
      // not -> default 1).
      var qty = positiveIntOr(qtyInputFor(form), 1);
      // Buy Now vs Request-a-Quote: the Buy-Now submitter carries data-rcds-goto="/cart",
      // so after adding we route to the quote-only cart (the warm "nothing charged online"
      // step). Request-a-Quote has no goto -> toast + stay. Both add ItemType=Quote; neither
      // touches a payment path (the cart's checkout button is gated off in quote-only mode).
      var submitter = e.submitter || (form.querySelector('[data-rcds-goto]'));
      var goTo = submitter && submitter.getAttribute ? submitter.getAttribute('data-rcds-goto') : null;
      e.preventDefault();
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: id, quantity: qty, properties: { ItemType: rcItemType } }] })
      })
        .then(readAdd(form))
        .then(function (res) {
          if (res === REFUSED) { return null; }
          if (goTo) { window.location.href = goTo; return null; }  // Add to Cart -> the cart
          showQuoteToast();
          return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
        })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (c) { if (c && typeof c.item_count === 'number') { setCartCount(c.item_count); } })
        .catch(function () { form.submit(); });
    }, false);
  }

  // Frequently Bought Together: multi-line add of every CHECKED row (anchor + companions)
  // in ONE /cart/add.js, plus a live running total. No-JS: the form's single hidden id
  // (the first row) POSTs on its own. Every line carries its OWN row's type (rowItemType
  // below). While this runs the native form never posts (the submit handler below).
  // Delegated + idempotent.
  function initFbt() {
    if (window.__rcFbtBound) return;
    window.__rcFbtBound = true;
    // The store's own money_format (layout/theme.liquid prints it as window.RCDS_MONEY_FORMAT,
    // 'US${{amount}}' on www.rolacase.us), so this total reads like every other price on the page.
    // No format printed: exactly the string this function returned before.
    function money(cents) {
      var n = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      var f = typeof window !== 'undefined' && window.RCDS_MONEY_FORMAT;
      return (f && typeof f === 'string' && f.indexOf('{{amount}}') !== -1) ? f.split('{{amount}}').join(n) : '$' + n;
    }
    function recalc(form) {
      var totalEl = form.querySelector('[data-rcds-fbt-total]');
      if (!totalEl) return;
      var sum = 0;
      form.querySelectorAll('[data-rcds-fbt-item]').forEach(function (row) {
        var cb = row.querySelector('input[type="checkbox"]');
        var price = parseInt(row.getAttribute('data-rcds-fbt-price'), 10) || 0;
        if (cb && cb.checked) sum += price;
      });
      totalEl.textContent = money(sum);
    }
    // One row's cart line type. snippets/rcds-fbt.liquid prints the buy gate's answer for THAT
    // row's product as data-rcds-fbt-itemtype. Only an explicit "Buy" is posted as a purchase: a
    // row without the attribute (a page cached before rows carried one) or with any other value
    // stays a Quote line, which is what every row posted before, so no markup can become a
    // purchase by accident. The same default as initQuoteAdd.
    function rowItemType(row) {
      return row.getAttribute('data-rcds-fbt-itemtype') === 'Buy' ? 'Buy' : 'Quote';
    }
    // The toast names where the POSTED lines went (the snippet words its button the same way):
    // quote lines only -> the words every add-to-quote uses; Buy lines only -> the cart; both ->
    // both. One /cart page holds the two groups.
    var FBT_CART_TOAST = 'Added to your cart';
    var FBT_MIXED_TOAST = 'Added to your cart and quote list';
    function showFbtToast(items) {
      var buy = items.filter(function (it) { return it.properties.ItemType === 'Buy'; }).length;
      if (!buy) { showQuoteToast(); return; }
      showToast(buy === items.length ? FBT_CART_TOAST : FBT_MIXED_TOAST, 'View cart', '/cart', false);
    }
    // initial total = sum of all pre-checked rows (anchor + companions), not just the
    // server-rendered anchor price.
    document.querySelectorAll('[data-rcds-fbt-form]').forEach(recalc);
    // live total on toggle
    document.addEventListener('change', function (e) {
      if (!e.target.matches || !e.target.matches('[data-rcds-fbt-toggle]')) return;
      var form = e.target.closest('[data-rcds-fbt-form]');
      if (form) recalc(form);
    }, false);
    // WHOLE-CARD toggle: clicking anywhere on a companion card flips its checkbox — the
    // tiny box is no longer the only hit target. Skip clicks on a real <a> (the title
    // link navigates) and on the checkbox/label itself (its native toggle already fires),
    // and skip the locked anchor card (disabled checkbox).
    document.addEventListener('click', function (e) {
      var item = e.target.closest ? e.target.closest('[data-rcds-fbt-item]') : null;
      if (!item) return;
      if (e.target.closest('a')) return;
      if (e.target.closest('.rcds-fbt__check')) return;
      var cb = item.querySelector('input[type="checkbox"]');
      if (!cb || cb.disabled) return;
      cb.checked = !cb.checked;
      cb.dispatchEvent(new Event('change', { bubbles: true }));
    }, false);
    // multi-add on submit
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('[data-rcds-fbt-form]')) return;
      if (!window.fetch) return;  // no fetch: the native single-id POST, what a no-JS visitor gets
      // From here on the native form never posts. It carries the first row's id only, so it would add
      // that one row (even one the shopper unticked) and none of the others.
      e.preventDefault();
      var items = [];
      form.querySelectorAll('[data-rcds-fbt-item]').forEach(function (row) {
        var cb = row.querySelector('input[type="checkbox"]');
        if (cb && cb.checked) {
          var id = row.getAttribute('data-rcds-fbt-variant');
          if (id) items.push({ id: id, quantity: 1, properties: { ItemType: rowItemType(row) } });
        }
      });
      if (!items.length) return;  // nothing ticked: nothing to add, so nothing happens
      var added = false;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items })
      })
        .then(readAdd(form))
        .then(function (res) {
          if (res === REFUSED) { return null; }
          added = true;
          showFbtToast(items);
          return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
        })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (c) { if (c && typeof c.item_count === 'number') { setCartCount(c.item_count); } })
        .catch(function () {
          // No answer from /cart/add.js (network down, or the answer lost): the error toast, never a
          // native re-post. A badge refresh failing after a good add is not an error. refreshCart()
          // re-reads the cart in case the add did land.
          if (added) { return; }
          refreshCart();
          showToast(ADD_FAILED, '', '', true);
        });
    }, false);
  }

  function boot() { tagGroups(); initReveal(); initParallax(); initCountUp(); initHeaderScroll(); initHeroVideo(); initQtyStepper(); initQuoteAdd(); initQuoteQtyFromLink(); initFbt(); initCartBadge(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
